
import Vue from 'vue';
import { Button } from '@descomplica/vue-components';
import { ANTICIPATE_WEEKLY_LISTS_STUDENT_IMAGE_URL } from '@/helpers/postgradStudyPlanning/constants';
import { PostgradAnalytics } from '@/helpers/analytics/postgradAnalytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { CourseModel } from '@descomplica/dex-models';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { WeekModel } from '@/models/studyPlanning/WeekModel';
import { TrackStudyPlanningAnticipation } from '@/helpers/analytics/types';
import { endOf, getDiffTime, startOf } from '@/helpers/date';
import { showPostgradErrorToast } from '@/helpers/postgradErrorToast';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';

export default Vue.extend({
  components: {
    Button,
  },
  props: {
    courseId: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      studentImage: ANTICIPATE_WEEKLY_LISTS_STUDENT_IMAGE_URL,
      isLoadingAnticipate: false,
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    studyPlanningWeek(): WeekModel {
      return this.$store.getters.studyPlanningWeek;
    },
    totalAnticipatedStudyPlanningWeeks(): number {
      const currentWeekPosition = this.$store.getters.studyPlanningWeek.position;
      const anticipatedWeeks = this.$store.getters.studyPlanningAnticipatedWeeks(
        this.studyPlanning.id,
      ) as WeekModel[];

      if (!anticipatedWeeks.length) return 0;

      const highestAnticipatedWeekPosition = Math.max(...anticipatedWeeks.map((w) => w.position));

      return highestAnticipatedWeekPosition - currentWeekPosition;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    defaultTrackingProps(): TrackStudyPlanningAnticipation {
      const studyPlanningTotalWeeks = getDiffTime(
        endOf(this.studyPlanning.expirationDate),
        startOf(this.studyPlanning.creationDate),
      ).weeks;

      return {
        course: this.currentCourse.name,
        category: this.currentCourse.descomplicaCategorySlug,
        current_study_planning_completion: Math.ceil(
          ((this.studyPlanningWeek.position + 1) / studyPlanningTotalWeeks) * 100,
        ),
        current_total_anticipated_study_planning_weeks: this.totalAnticipatedStudyPlanningWeeks,
        ...this.getTrialStatusByDescomplicaCourseId,
      };
    },
  },
  mounted() {
    PostgradAnalytics.trackEvent<TrackStudyPlanningAnticipation>(
      EVENT_LABELS.postgradStudyPlanningAnticipationCardViewed,
      this.defaultTrackingProps,
    );
  },
  methods: {
    async anticipateWeekLists() {
      this.isLoadingAnticipate = true;

      try {
        const anticipatedSuccessfully = await this.$store.dispatch(
          'ANTICIPATE_STUDY_PLANNING_LISTS',
          {
            studyPlanningId: this.studyPlanning.id,
            courseId: this.courseId,
          },
        );

        if (!anticipatedSuccessfully) {
          this.handleAnticipationError(
            new Error('Study planning anticipation not allowed by the server'),
          );

          return;
        }

        PostgradAnalytics.trackEvent<TrackStudyPlanningAnticipation>(
          EVENT_LABELS.postgradStudyPlanningAnticipated,
          this.defaultTrackingProps,
        );
      } catch (err) {
        this.handleAnticipationError(err);
      } finally {
        this.isLoadingAnticipate = false;
      }
    },
    handleAnticipationError(err: unknown): void {
      PostgradAnalytics.trackEvent<TrackStudyPlanningAnticipation>(
        EVENT_LABELS.postgradStudyPlanningAnticipationError,
        this.defaultTrackingProps,
      );

      showPostgradErrorToast(
        err as Error,
        this.$t('postgradStudyPlanning.anticipateWeeklyListsErrorToastMessage').toString(),
        { longMessage: true },
      );
    },
  },
});
